import { useTranslation } from 'react-i18next';
import type { ProNotificationOrderProgress } from '@erp/domain/pro';
import { NOTIFICATION_PALETTE } from '@/lib/notification-view';

const STEP_LABELS: Record<number, [key: string, fallback: string]> = {
  0: ['pro.notif.step.pending', 'En attente'],
  1: ['pro.notif.step.confirmed', 'Confirmée'],
  2: ['pro.notif.step.packed', 'Emballée'],
  3: ['pro.notif.step.shipped', 'Expédiée'],
  4: ['pro.notif.step.delivered', 'Livrée'],
};

/**
 * Le parcours de la commande en cinq points. C'est le repère visuel que
 * l'électricien lit avant même le texte : « où en est ma commande ».
 *
 * `retournee` / `rejetee` sortent du parcours nominal — on ne peut pas les
 * placer sur la frise, alors on la grise entièrement et on affiche l'état
 * terminal en toutes lettres.
 */
export function OrderProgress({
  progress,
}: {
  progress: ProNotificationOrderProgress;
}) {
  const { t } = useTranslation();

  if (progress.interrupted) {
    const palette =
      progress.status === 'rejetee'
        ? NOTIFICATION_PALETTE.danger
        : NOTIFICATION_PALETTE.neutral;
    return (
      <div className="flex items-center gap-1.5 mt-2">
        {Array.from({ length: progress.total }).map((_, i) => (
          <span
            key={i}
            className="h-1 flex-1 rounded-full"
            style={{ background: 'var(--pro-line)' }}
          />
        ))}
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0"
          style={{ background: palette.bg, color: palette.fg }}
        >
          {progress.status === 'rejetee'
            ? t('pro.notif.step.rejected', 'Rejetée')
            : t('pro.notif.step.returned', 'Retournée')}
        </span>
      </div>
    );
  }

  const current = progress.step ?? 0;
  const label = STEP_LABELS[current];

  return (
    <div className="mt-2">
      <div
        className="flex items-center gap-1.5"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={progress.total}
        aria-valuenow={current + 1}
        aria-label={label ? t(label[0], label[1]) : undefined}
      >
        {Array.from({ length: progress.total }).map((_, i) => (
          <span
            key={i}
            className="h-1 flex-1 rounded-full"
            style={{
              background:
                i <= current ? 'var(--pro-primary)' : 'var(--pro-line)',
              // Le point courant est plus épais : l'œil trouve l'étape sans
              // avoir à compter les segments allumés.
              height: i === current ? 4 : 3,
            }}
          />
        ))}
      </div>
      {label && (
        <div
          className="text-[10px] font-medium mt-1"
          style={{ color: 'var(--pro-primary-text)' }}
        >
          {t(label[0], label[1])}
          <span style={{ color: 'var(--pro-text-grey-light)' }}>
            {' '}
            · {current + 1}/{progress.total}
          </span>
        </div>
      )}
    </div>
  );
}
